import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import {
  type SettlementChangeView,
  type SettlementModeCode,
  type SettlementPaymentMethodCode,
  type SettlementPeriodPlanKind,
  type SettlementPlanView,
  type SettlementReportFilter,
  type SettlementReportRowView,
  type WalletMoneyWire,
  SETTLEMENT_PAYMENT_METHOD_CODES,
  SETTLEMENT_PERIOD_PLAN_KINDS,
  SETTLEMENT_REPORT_FILTERS,
} from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  maySelfApprove,
  paymentDraftIssue,
  planDraftIssue,
  settlementApproveBlock,
  settlementTone,
  summarizeSettlement,
  walletAuthority,
} from '../../src/index';
import { useAdmin } from '../session';
import { PersonName } from '../components/person-name';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, CheckRow, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Field, MoneyInput, Notice, PageHeader, Panel, SegmentedControl, Select, StatCard, Table, TextInput, FormRow, EmptyState, Grid, Ink, Row, Text } from '@vello/web-theme';

/**
 * Finance › Acertos (W9-DRIVER-SETTLEMENT-MODES, D469, ADR-0087; DevMobility F-011–F-014, F-050), over
 * `SettlementAdminApi` (`/v1/admin/settlement/*`). Per market, chosen from the operator's scope — never typed:
 *
 * - **Relatório** (F-013): every driver's mode, standing, balance and the period's movements (local days), filtered by the
 *   tolerances or the overdue; select drivers and PROPOSE settlement payments (F-014) — TOTAL (clears the debt) or the
 *   PERIOD's net debt, by a method. Each becomes a wave-8 four-eyes CREDIT adjustment that ANOTHER operator applies in
 *   Carteiras › Aprovações. A driver row also proposes his mode (only the modes the market's `settlement.policy` allows) or
 *   the end of his paid period.
 * - **Inadimplentes** (F-050): the overdue postpaid drivers, each with "propor adiamento" of the due date that made him so.
 * - **Aprovações**: the four-eyes inbox of settlement changes; approve is hidden from the proposer and from the driver it
 *   is about (the server refuses them anyway), a rejection needs a reason.
 * - **Planos** (F-011): the period plans of the market — create one, take it off sale or back (a new version).
 * Money is typed through the design-system `MoneyInput` in the market's currency. A failed read is an ERROR, never an
 * empty list. No native dialog is ever opened.
 */

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string } | undefined;
type Tab = 'report' | 'delinquents' | 'changes' | 'plans';

const note = (text: string): ReactElement => <EmptyState title={text} />;

export function DriverSettlementScreen(): ReactElement {
  const { runtime, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { fail, v, reason, refusal: refusalOf } = useVocab();
  const { mayView, mayManage, mayApprove } = walletAuthority(grants);
  // Owner 2026-10-12: the market IS the header scope's city — no second market selector on this screen.
  const { market: scopeMarket, choices } = useScopeMarket();
  const market = scopeMarket ?? '';
  const [tab, setTab] = useState<Tab>('report');
  const [loaded, setLoaded] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [filter, setFilter] = useState<SettlementReportFilter | ''>('');
  const [report, setReport] = useState<{ rows: readonly SettlementReportRowView[]; currency: string; from: string; to: string } | undefined>(undefined);
  const [delinquents, setDelinquents] = useState<readonly SettlementReportRowView[] | undefined>(undefined);
  const [changes, setChanges] = useState<readonly SettlementChangeView[] | undefined>(undefined);
  const [plans, setPlans] = useState<readonly SettlementPlanView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [basis, setBasis] = useState<'TOTAL' | 'PERIOD'>('TOTAL');
  const [method, setMethod] = useState<SettlementPaymentMethodCode>('CASH');
  const [payNote, setPayNote] = useState('');
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [focus, setFocus] = useState<SettlementReportRowView | undefined>(undefined);
  const [focusMode, setFocusMode] = useState<SettlementModeCode | ''>('');
  const [focusNote, setFocusNote] = useState('');
  const [planName, setPlanName] = useState('');
  const [planKind, setPlanKind] = useState<SettlementPeriodPlanKind>('DAYS');
  const [planCount, setPlanCount] = useState('30');
  const [planPrice, setPlanPrice] = useState<bigint | undefined>(undefined);
  const seq = useRef(0);

  const money = useCallback(
    (m: WalletMoneyWire | null | undefined): string =>
      m === null || m === undefined ? '—' : /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : `${m.amountMinor} ${m.currency}`,
    [lang],
  );
  const refusal = useCallback(
    (e: { code: string; reasonCode?: string }, fallback: string): string => {
      return refusalOf('refusal.admin.driver_settlement', e) ?? t(fallback, { code: fail(e) });
    },
    [t, refusalOf],
  );
  const settlementReason = (code: string): string => v('driver_wallet.settlement_reason', code);

  const load = useCallback(
    async (m: string, background = false): Promise<void> => {
      const mine = ++seq.current;
      const target = m.trim();
      // A background read (tick, or a new period / filter) keeps the rows, the selection and the open driver on screen;
      // only a new market starts from a clean desk.
      if (!background) {
        setLoaded(target);
        setReport(undefined);
        setDelinquents(undefined);
        setChanges(undefined);
        setPlans(undefined);
        setError(undefined);
        setSelected(new Set());
        setFocus(undefined);
      }
      if (target === '' || !mayView) return;
      const opts = { ...(from !== '' ? { from } : {}), ...(to !== '' ? { to } : {}), ...(filter !== '' ? { filter } : {}) };
      const [r, d, c, p] = await Promise.all([runtime.settlement.report(target, opts), runtime.settlement.delinquents(target), runtime.settlement.changes(target), runtime.settlement.plans(target)]);
      if (mine !== seq.current) return;
      if (r.ok) {
        setError(undefined);
        setReport({ rows: r.value.rows, currency: r.value.currency, from: r.value.from, to: r.value.to });
      } else setError(refusal(r.error, 'settle.error'));
      if (d.ok) setDelinquents(d.value.rows);
      if (c.ok) setChanges(c.value.changes);
      if (p.ok) setPlans(p.value.plans);
    },
    [runtime, mayView, from, to, filter, refusal],
  );
  // The desk follows the header scope's market, the period and the filter by itself, and stays current in the background.
  const readMarket = useRef<string | undefined>(undefined);
  useEffect(() => {
    const marketChanged = readMarket.current !== market;
    readMarket.current = market;
    void load(market, !marketChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market, from, to, filter]);
  useAutoRefresh(() => load(market, true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' && mayView });

  const propose = async (body: { driverId: string; kind: 'ASSIGN_MODE' | 'POSTPONE_DUE' | 'SET_ACCESS_UNTIL' | 'CLEAR_MODE'; mode?: SettlementModeCode; dueDate?: string; accessUntil?: string; note?: string }): Promise<void> => {
    setBusy(true);
    const res = await runtime.settlement.proposeChange({ marketId: loaded, ...body });
    setBusy(false);
    setMsg(res.ok ? { tone: 'success', text: t('settle.proposed') } : { tone: 'danger', text: refusal(res.error, 'settle.error') });
    if (res.ok) {
      setFocus(undefined);
      const c = await runtime.settlement.changes(loaded);
      if (c.ok) setChanges(c.value.changes);
    }
  };
  const decide = async (changeId: string, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    const why = reasons[changeId]?.trim();
    setBusy(true);
    const res = await runtime.settlement.decideChange(changeId, { marketId: loaded, decision, ...(why !== undefined && why !== '' ? { reason: why } : {}) });
    setBusy(false);
    setMsg(res.ok ? { tone: 'success', text: t('settle.decided') } : { tone: 'danger', text: refusal(res.error, 'settle.error') });
    if (res.ok) void load(loaded);
  };
  const payIssue = paymentDraftIssue({ driverIds: [...selected], basis, from: report?.from ?? '', to: report?.to ?? '' });
  const pay = async (): Promise<void> => {
    if (payIssue !== undefined || report === undefined) return;
    setBusy(true);
    const res = await runtime.settlement.proposePayments({ marketId: loaded, driverIds: [...selected], basis, method, ...(basis === 'PERIOD' ? { from: report.from, to: report.to } : {}), ...(payNote.trim() !== '' ? { note: payNote.trim() } : {}) });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error, 'settle.error') });
      return;
    }
    const proposed = res.value.results.filter((r) => r.outcome === 'PROPOSED').length;
    setMsg({ tone: 'success', text: t('settle.pay.done', { proposed: String(proposed), nothing: String(res.value.results.length - proposed) }) });
    setSelected(new Set());
  };
  const planIssue = planDraftIssue({ name: planName, kind: planKind, count: Number(planCount), priceMinor: planPrice });
  const savePlan = async (input: { planId?: string; expectedVersion?: number; name: string; kind: SettlementPeriodPlanKind; count: number; priceMinor: string; active: boolean; sortOrder?: number }): Promise<void> => {
    setBusy(true);
    const res = await runtime.settlement.savePlan({ marketId: loaded, ...input });
    setBusy(false);
    setMsg(res.ok ? { tone: 'success', text: t('settle.plan.saved') } : { tone: 'danger', text: refusal(res.error, 'settle.error') });
    if (res.ok) {
      const p = await runtime.settlement.plans(loaded);
      if (p.ok) setPlans(p.value.plans);
    }
  };

  const summary = report !== undefined ? summarizeSettlement(report.rows) : undefined;
  const standingBadge = (r: SettlementReportRowView): ReactElement => <Badge tone={settlementTone(r.settlement)}>{settlementReason(r.settlement.reasonCode)}</Badge>;
  const toggle = (driverId: string, on: boolean): void => {
    const next = new Set(selected);
    if (on) next.add(driverId);
    else next.delete(driverId);
    setSelected(next);
  };
  const reportColumns = (withSelect: boolean, delinquent: boolean) => [
    ...(withSelect ? [{ key: 'select', header: t('settle.col.select'), render: (r: SettlementReportRowView) => <CheckRow id={`settle-sel-${r.driverId}`} checked={selected.has(r.driverId)} onChange={(on) => toggle(r.driverId, on)}><span className="vello-sr-only"><PersonName id={r.driverId} /></span></CheckRow> }] : []),
    { key: 'driver', header: t('settle.col.driver'), render: (r: SettlementReportRowView) => <PersonName id={r.driverId} /> },
    { key: 'mode', header: t('settle.col.mode'), render: (r: SettlementReportRowView) => v('driver_wallet.settlement_mode', r.settlement.mode) },
    { key: 'standing', header: t('settle.col.standing'), render: standingBadge },
    { key: 'balance', header: t('settle.col.balance'), numeric: true, render: (r: SettlementReportRowView) => <span className="tnum">{money(r.balance)}</span> },
    { key: 'net', header: t('settle.col.periodNet'), numeric: true, render: (r: SettlementReportRowView) => <span className="tnum">{money(r.periodNet)}</span> },
    { key: 'due', header: t('settle.col.due'), render: (r: SettlementReportRowView) => <span className="tnum">{r.settlement.postpaid?.enforcedDueDate ?? r.settlement.postpaid?.currentDueDate ?? r.settlement.period?.accessUntil?.slice(0, 10) ?? '—'}</span> },
    ...(mayManage
      ? [{
          key: 'actions',
          header: t('settle.col.actions'),
          render: (r: SettlementReportRowView) =>
            delinquent && r.settlement.postpaid?.enforcedDueDate !== null && r.settlement.postpaid?.enforcedDueDate !== undefined ? (
              <Button type="button" size="sm" disabled={busy} onClick={() => void propose({ driverId: r.driverId, kind: 'POSTPONE_DUE', dueDate: r.settlement.postpaid?.enforcedDueDate ?? '' })}>{t('settle.action.postpone')}</Button>
            ) : (
              <Button type="button" size="sm" variant="ghost" onClick={() => { setFocus(r); setFocusMode(''); setFocusNote(''); }}>{t('settle.action.assign')}</Button>
            ),
        }]
      : []),
  ];

  return (
    <>
      <PageHeader title={t('settle.title')} subtitle={t('settle.subtitle')} />
      {!mayView ? (
        <Panel>{note(t('settle.noPermission'))}</Panel>
      ) : scopeMarket === undefined ? (
        <ScopeMarketNotice choices={choices} />
      ) : (
        <>
          <Panel>
            <form onSubmit={(e) => e.preventDefault()}>
              <FormRow>
                <Field label={t('settle.from')} htmlFor="settle-from">
                  <TextInput id="settle-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </Field>
                <Field label={t('settle.to')} htmlFor="settle-to">
                  <TextInput id="settle-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </Field>
                <Field label={t('settle.filter')} htmlFor="settle-filter">
                  <Select id="settle-filter" value={filter} onChange={(e) => setFilter(e.target.value as SettlementReportFilter | '')}>
                    <option value="">{t('settle.filter.all')}</option>
                    {SETTLEMENT_REPORT_FILTERS.map((f) => (
                      <option key={f} value={f}>{v('driver.settlement_report_filter', f)}</option>
                    ))}
                  </Select>
                </Field>
              </FormRow>
            </form>
            {error !== undefined && <Notice tone="danger">{error}</Notice>}
            {msg !== undefined && focus === undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
          </Panel>

          {loaded !== '' && error === undefined && (
            <div>
              <SegmentedControl<Tab>
                ariaLabel={t('settle.title')}
                value={tab}
                onChange={setTab}
                options={[
                  { id: 'report', label: t('settle.tab.report') },
                  { id: 'delinquents', label: t('settle.tab.delinquents') },
                  { id: 'changes', label: t('settle.tab.changes') },
                  { id: 'plans', label: t('settle.tab.plans') },
                ]}
              />
            </div>
          )}

          {tab === 'report' && (
            <>
              {summary !== undefined && report !== undefined && (
                <Grid min="sm" gap={4}>
                  <StatCard label={t('settle.summary.owed')} value={money({ amountMinor: summary.owedMinor.toString(), currency: summary.currency ?? report.currency })} hint={`${report.from} → ${report.to}`} />
                  <StatCard label={t('settle.summary.overdue')} value={String(summary.overdue)} tone={summary.overdue > 0 ? 'danger' : undefined} />
                  <StatCard label={t('settle.summary.blocked')} value={String(summary.blocked)} tone={summary.blocked > 0 ? 'warning' : undefined} />
                </Grid>
              )}
              <Panel padded={false}>
                <Table
                  columns={reportColumns(mayManage, false)}
                  rows={report?.rows ?? []}
                  rowKey={(r) => r.driverId}
                  loading={loaded !== '' && report === undefined && error === undefined}
                  loadingLabel={t('ui.loading')}
                  error={error !== undefined ? t('ui.loadFailed') : undefined}
                  onRetry={() => void load(market)}
                  retryLabel={t('ui.retry')}
                  empty={note(loaded === '' ? t('settle.needsMarket') : t('settle.empty'))}
                />
              </Panel>
              <Drawer open={focus !== undefined && mayManage} onClose={() => setFocus(undefined)} labelledBy="settle-focus-title">
                {focus !== undefined && mayManage && (
                  <>
                    <DrawerHeader titleId="settle-focus-title" title={<PersonName id={focus.driverId} />} subtitle={<IdRef value={focus.driverId} copy />} onClose={() => setFocus(undefined)} closeLabel={t('ui.close')} />
                    <DrawerBody>
                      <DrawerStack>
                        {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
                        {/* D838: where the mode in force comes from - his own, his group's or the market's default. */}
                        <Text as="p" role="caption" tone="secondary" data-testid="settle-focus-source">
                          {v('driver_wallet.settlement_mode', focus.settlement.mode)} · {v('driver_wallet.settlement_reason', focus.settlement.modeReasonCode)}
                        </Text>
                        <FormRow>
                          <Field label={t('settle.col.mode')} htmlFor="settle-focus-mode">
                            <Select id="settle-focus-mode" value={focusMode} onChange={(e) => setFocusMode(e.target.value as SettlementModeCode | '')}>
                              <option value="">—</option>
                              {focus.settlement.allowedModes.map((m) => (
                                <option key={m} value={m}>{v('driver_wallet.settlement_mode', m)}</option>
                              ))}
                            </Select>
                          </Field>
                          <Field label={t('settle.note')} htmlFor="settle-focus-note">
                            <TextInput id="settle-focus-note" value={focusNote} maxLength={500} onChange={(e) => setFocusNote(e.target.value)} />
                          </Field>
                        </FormRow>
                      </DrawerStack>
                    </DrawerBody>
                    <DrawerFooter>
                      <Button type="button" variant="ghost" onClick={() => setFocus(undefined)}>{t('ui.close')}</Button>
                      <Button type="button" disabled={busy || focusMode === ''} onClick={() => void propose({ driverId: focus.driverId, kind: 'ASSIGN_MODE', mode: focusMode as SettlementModeCode, ...(focusNote.trim() !== '' ? { note: focusNote.trim() } : {}) })}>{t('settle.action.assign')}</Button>
                      {focus.settlement.modeReasonCode === 'SETTLEMENT_MODE_ASSIGNED' && (
                        <Button type="button" variant="secondary" disabled={busy} onClick={() => void propose({ driverId: focus.driverId, kind: 'CLEAR_MODE', ...(focusNote.trim() !== '' ? { note: focusNote.trim() } : {}) })}>{t('settle.action.inherit')}</Button>
                      )}
                      {focus.settlement.mode === 'PREPAID_PERIOD' && (
                        <Button type="button" disabled={busy} onClick={() => void propose({ driverId: focus.driverId, kind: 'SET_ACCESS_UNTIL', accessUntil: new Date().toISOString(), ...(focusNote.trim() !== '' ? { note: focusNote.trim() } : {}) })}>{t('settle.action.endAccess')}</Button>
                      )}
                    </DrawerFooter>
                  </>
                )}
              </Drawer>
              {mayManage && report !== undefined && (
                <Panel>
                  <h3>{t('settle.pay.title')}</h3>
                  <FormRow
                    action={
                      <Button type="button" disabled={busy || payIssue !== undefined} onClick={() => void pay()}>{t('settle.pay.propose')}</Button>
                    }
                  >
                    <Field label={t('settle.pay.basis')} htmlFor="settle-pay-basis">
                      <Select id="settle-pay-basis" value={basis} onChange={(e) => setBasis(e.target.value === 'PERIOD' ? 'PERIOD' : 'TOTAL')}>
                        <option value="TOTAL">{t('settle.pay.basis.TOTAL')}</option>
                        <option value="PERIOD">{t('settle.pay.basis.PERIOD')}</option>
                      </Select>
                    </Field>
                    <Field label={t('settle.pay.method')} htmlFor="settle-pay-method">
                      <Select id="settle-pay-method" value={method} onChange={(e) => setMethod(e.target.value as SettlementPaymentMethodCode)}>
                        {SETTLEMENT_PAYMENT_METHOD_CODES.map((m) => (
                          <option key={m} value={m}>{v('driver_wallet.settlement_payment_method', m)}</option>
                        ))}
                      </Select>
                    </Field>
                    <Field label={t('settle.note')} htmlFor="settle-pay-note">
                      <TextInput id="settle-pay-note" value={payNote} maxLength={500} onChange={(e) => setPayNote(e.target.value)} />
                    </Field>
                  </FormRow>
                </Panel>
              )}
            </>
          )}

          {tab === 'delinquents' && (
            <Panel padded={false}>
              <Table columns={reportColumns(false, true)} rows={delinquents ?? []} rowKey={(r) => r.driverId} loading={delinquents === undefined} loadingLabel={t('ui.loading')} empty={note(t('settle.empty'))} />
            </Panel>
          )}

          {tab === 'changes' && (
            <Panel padded={false}>
              <Table
                columns={[
                  { key: 'kind', header: t('settle.col.mode'), render: (c: SettlementChangeView) => `${v('driver_wallet.settlement_change_kind', c.kind)}${c.mode !== null ? ` · ${v('driver_wallet.settlement_mode', c.mode)}` : ''}${c.dueDate !== null ? ` · ${c.dueDate}` : ''}${c.accessUntil !== null ? ` · ${c.accessUntil.slice(0, 16)}` : ''}` },
                  { key: 'driver', header: t('settle.col.target'), render: (c: SettlementChangeView) => (c.groupId !== undefined && c.groupId !== null ? <>{t('settle.target.group')} <IdRef value={c.groupId} /></> : c.driverId !== null ? <PersonName id={c.driverId} /> : '—') },
                  { key: 'by', header: t('settle.note'), render: (c: SettlementChangeView) => <>{c.proposedByLabel ?? <PersonName id={c.proposedBy} />}{c.note !== null ? ` — ${c.note}` : ''}</> },
                  {
                    key: 'decide',
                    header: t('settle.col.actions'),
                    render: (c: SettlementChangeView) => {
                      const block = settlementApproveBlock(c, me?.id, mayApprove, selfApproval);
                      if (block === 'NOT_PENDING') return v('driver_wallet.settlement_change_kind', c.kind);
                      if (block !== undefined) return <Ink tone="tertiary">{v('driver_wallet.settlement_decision_block', block)}</Ink>;
                      return (
                        <Row gap={2} align="center" wrap>
                          <TextInput id={`settle-why-${c.changeId}`} aria-label={t('settle.decisionReason')} placeholder={t('settle.decisionReason')} value={reasons[c.changeId] ?? ''} maxLength={500} onChange={(e) => setReasons({ ...reasons, [c.changeId]: e.target.value })} />
                          <Button type="button" size="sm" disabled={busy} onClick={() => void decide(c.changeId, 'APPROVE')}>{t('settle.approve')}</Button>
                          <Button type="button" size="sm" variant="ghost" disabled={busy || (reasons[c.changeId] ?? '').trim() === ''} onClick={() => void decide(c.changeId, 'REJECT')}>{t('settle.reject')}</Button>
                        </Row>
                      );
                    },
                  },
                ]}
                rows={changes ?? []}
                rowKey={(c) => c.changeId}
                loading={changes === undefined}
                loadingLabel={t('ui.loading')}
                empty={note(t('settle.empty'))}
              />
            </Panel>
          )}

          {tab === 'plans' && (
            <>
              <Panel padded={false}>
                <Table
                  columns={[
                    { key: 'name', header: t('settle.plan.name'), render: (p: SettlementPlanView) => p.name },
                    { key: 'kind', header: t('settle.plan.kind'), render: (p: SettlementPlanView) => `${v('driver_wallet.period_plan_kind', p.kind)} · ${p.count}` },
                    { key: 'price', header: t('settle.plan.price'), numeric: true, render: (p: SettlementPlanView) => <span className="tnum">{money(p.price)}</span> },
                    { key: 'active', header: t('settle.col.standing'), render: (p: SettlementPlanView) => <Badge tone={p.active ? 'success' : 'neutral'}>{t(p.active ? 'settle.plan.active' : 'settle.plan.inactive')}</Badge> },
                    ...(mayManage
                      ? [{ key: 'toggle', header: t('settle.col.actions'), render: (p: SettlementPlanView) => <Button type="button" size="sm" variant="ghost" disabled={busy || p.scope !== 'market'} onClick={() => void savePlan({ planId: p.planId, expectedVersion: p.version, name: p.name, kind: p.kind as SettlementPeriodPlanKind, count: p.count, priceMinor: p.price.amountMinor, active: !p.active, sortOrder: p.sortOrder })}>{t(p.active ? 'settle.plan.deactivate' : 'settle.plan.activate')}</Button> }]
                      : []),
                  ]}
                  rows={plans ?? []}
                  rowKey={(p) => p.planId}
                  loading={plans === undefined}
                  loadingLabel={t('ui.loading')}
                  empty={note(t('settle.plan.empty'))}
                />
              </Panel>
              {mayManage && report !== undefined && (
                <Panel>
                  <FormRow
                    action={
                      <Button type="button" disabled={busy || planIssue !== undefined} onClick={() => void savePlan({ name: planName.trim(), kind: planKind, count: Number(planCount), priceMinor: (planPrice ?? 0n).toString(), active: true })}>{t('settle.plan.create')}</Button>
                    }
                  >
                    <Field label={t('settle.plan.name')} htmlFor="settle-plan-name">
                      <TextInput id="settle-plan-name" value={planName} maxLength={80} onChange={(e) => setPlanName(e.target.value)} />
                    </Field>
                    <Field label={t('settle.plan.kind')} htmlFor="settle-plan-kind">
                      <Select id="settle-plan-kind" value={planKind} onChange={(e) => setPlanKind(e.target.value as SettlementPeriodPlanKind)}>
                        {SETTLEMENT_PERIOD_PLAN_KINDS.map((k) => (
                          <option key={k} value={k}>{v('driver_wallet.period_plan_kind', k)}</option>
                        ))}
                      </Select>
                    </Field>
                    <Field label={t('settle.plan.count')} htmlFor="settle-plan-count">
                      <TextInput id="settle-plan-count" inputMode="numeric" value={planCount} onChange={(e) => setPlanCount(e.target.value.replace(/\D/g, '').slice(0, 3))} />
                    </Field>
                    {isIso4217Currency(report.currency) && (
                      <MoneyInput id="settle-plan-price" label={t('settle.plan.price')} currency={report.currency} locale={lang} valueMinor={planPrice} onChangeMinor={setPlanPrice} issueText={(i) => v('format.money_input_issue', i)} />
                    )}
                  </FormRow>
                  {planIssue !== undefined && planName !== '' && <Notice tone="warning">{reason(planIssue)}</Notice>}
                </Panel>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
